import {
    initializeApollo,
    getAuthor,
    getGraphQLData,
} from '@zillow/cms-blocks';
import { STICKY_SIDEBAR_TOP_PX } from '@zillow/zrs-cms';
import { GetStaticPaths, GetStaticProps } from 'next';
import React from 'react';

import { REVALIDATION_INTERVAL } from '../../server/config';
import type { AuthorSocialLinks } from '../components/AuthorPage/AuthorPage.types';
import type { TMenuItem } from '../components/Menu/Menu.types';
import { Post } from '../types/shared';

export type AuthorPageContentProps = {
    author: {
        id: string;
        name: string;
        head?: string;
        description?: string;
        link?: string;
        avatar?: string;
        jobTitle?: string;
        socialLinks?: AuthorSocialLinks;
        expertise?: string[];
    };
    menu?: TMenuItem[];
    posts: Post[];
    total: number;
    pages: number;
    initialPage: number;
    preview?: boolean;
    /** Site identifier for clickstream analytics */
    siteSlug?: string;
};

export type LayoutComponentProps = {
    children: React.ReactNode;
    head?: string;
    menu?: unknown;
    preview?: boolean;
    [key: string]: unknown;
};

type BuildPrimaryAuthorPageInputs = {
    site: string;
    LayoutComponent: React.ComponentType<LayoutComponentProps>;
    ContentComponent: React.ComponentType<
        AuthorPageContentProps & { sidebarTop?: string }
    >;
    additionalLayoutProps?: Record<string, unknown>;
    skipWithD?: boolean;
    menuPropName?: 'menu' | 'submenu';
    postsPerPage?: string;
    hasStickyNav?: boolean;
};

export async function buildPrimaryAuthorPage(
    inputs: BuildPrimaryAuthorPageInputs
) {
    const getStaticProps: GetStaticProps<AuthorPageContentProps> = async ({
        params,
    }) => {
        const { slug } = params || {};
        // Support /author/[username]/page/[page] style
        const slugArray = Array.isArray(slug) ? slug : [slug];
        const hasPageNumber =
            slugArray.length >= 3 && slugArray[slugArray.length - 2] === 'page';
        const pageNumber = hasPageNumber
            ? Number(slugArray[slugArray.length - 1])
            : 1;
        const username = hasPageNumber
            ? slugArray[slugArray.length - 3]
            : slugArray[slugArray.length - 1];

        if (!username) return { notFound: true };

        initializeApollo();

        let author, settings;
        try {
            const result = await getAuthor({
                variables: {
                    site: inputs.site,
                    id: username,
                    idType: 'SLUG',
                    authorPostsType: 'POST',
                    authorPostsPage: pageNumber.toString(),
                    authorPostsPerPage: inputs.postsPerPage || '12',
                    withSettings: true,
                },
                fetchPolicy: 'no-cache',
            });
            author = result.author;
            settings = result.settings;
        } catch (error) {
            if (
                error?.message ===
                getGraphQLData.ErrorCodes.ZG_GRAPH_PAGINATION_ERROR
            ) {
                return {
                    notFound: true,
                };
            }

            throw error;
        }

        if (!author?.user) return { notFound: true };

        const { user, posts } = author;

        const props: AuthorPageContentProps = {
            author: user,
            menu: settings?.menu || null,
            posts: posts?.posts || [],
            total: posts?.total || 0,
            pages: posts?.pages || 1,
            initialPage: pageNumber,
            siteSlug: inputs.site,
        };

        return {
            props,
            revalidate: REVALIDATION_INTERVAL,
        };
    };

    const getStaticPaths: GetStaticPaths = async () => ({
        paths: [],
        fallback: 'blocking',
    });

    const PageComponent: React.FC<AuthorPageContentProps> = (props) => {
        const layoutProps: LayoutComponentProps = {
            ...inputs.additionalLayoutProps,
            preview: props.preview,
            head: props.author?.head,
            children: (
                <inputs.ContentComponent
                    {...props}
                    sidebarTop={
                        inputs.hasStickyNav
                            ? `${STICKY_SIDEBAR_TOP_PX}px`
                            : undefined
                    }
                />
            ),
        };

        // Add menu props based on specified prop name
        if (props.menu) {
            const propName = inputs.menuPropName || 'menu';
            layoutProps[propName] = props.menu;
        }

        return <inputs.LayoutComponent {...layoutProps} />;
    };

    return {
        getStaticProps,
        getStaticPaths,
        Page: PageComponent,
        WrappedPage: PageComponent,
    };
}
